/* eslint-disable react-hooks/exhaustive-deps */
// Backup of RailSettings.tsx logic prior to the `multiRailStation` feature flag rollout.
// Used as the fallback implementation while `multiRailStation` is `false`. Safe to delete
// once the flag is retired.
import { useState } from "react";

import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import { useAuth } from "react-oidc-context";
import SearchIcon from "@mui/icons-material/Search";
import CustomerFilterArrowIcon from "../../../svg/selectRailStationCustomerDownArrow.svg";
import NitroService from "../../../utils/nitro.service";
import ILocation from "../../../types/ILocation";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { useIsMobile } from "../../../hooks/useIsMobile";
import RailStationLookupPanel from "../../../panels/mobile/railStationLookupPanel/RailStationLookupPanel";
import RailStationCarrierReviewPanel from "../../../panels/mobile/railStationLookupPanel/RailStationCarrierReviewPanel";
import RailStationAddCarrierPanel from "../../../panels/mobile/railStationLookupPanel/RailStationAddCarrierPanel";
import { toModes } from "../../../utils/utils";

const DisabledRailSettings = ({ location }: { location: ILocation }) => {
  const carrierList = location.carriers?.map((carrier) => carrier.id);
  let stationDisplay: string | null = null;
  if (location.stationState) {
    stationDisplay = carrierList.length
      ? `${location.stationState} (${carrierList.join(", ")})`
      : location.stationState;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        paddingRight: 3,
        paddingTop: 3,
        paddingBottom: 3,
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 14, color: "#62748e", lineHeight: "14px" }}>
          Station &amp; Carrier(s)
        </div>
        <div
          id="rail-station-name-old"
          style={{
            fontSize: 14,
            color: stationDisplay ? "#0a0a0a" : "#878787",
            lineHeight: "17.5px",
            padding: "4px 8px",
            borderRadius: 3.5,
          }}
        >
          {stationDisplay || (
            <span style={{ fontStyle: "italic" }}>No station selected</span>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 14, color: "#62748e", lineHeight: "14px" }}>
          Equipment types
        </div>
        <div
          style={{
            fontSize: 14,
            color:
              location.equipmentTypes && location.equipmentTypes.length > 0
                ? "#0a0a0a"
                : "#878787",
            lineHeight: "17.5px",
            padding: "4px 8px",
            borderRadius: 3.5,
          }}
        >
          {location.equipmentTypes && location.equipmentTypes.length > 0 ? (
            location.equipmentTypes.join(", ")
          ) : (
            <span style={{ fontStyle: "italic" }}>No equipment type</span>
          )}
        </div>
      </div>
    </div>
  );
};

const EquipmentDropdownIcon = ({
  style,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span {...rest} style={{ ...style, transform: "none" }}>
    <img
      src={CustomerFilterArrowIcon}
      alt=""
      style={{ width: 16, height: 16, pointerEvents: "none" }}
    />
  </span>
);

const RailSettingsOLD = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  originalLocation: _originalLocation,
  location,
  setLocation,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  zipCode: _zipCode,
  disabled,
}: {
  originalLocation?: ILocation;
  location: ILocation;
  setLocation: (location: ILocation) => void;
  zipCode: string;
  disabled?: boolean;
}) => {
  const auth = useAuth();
  const isMobile = useIsMobile();
  const [showRailStationLookupPanel, setShowRailStationLookupPanel] =
    useState(false);
  const [showRailCarrierReviewPanel, setShowRailCarrierReviewPanel] =
    useState(false);
  const [showRailAddCarrierPanel, setShowRailAddCarrierPanel] = useState(false);
  const [selectedLookupStation, setSelectedLookupStation] = useState("");
  const [selectedLookupScrsId, setSelectedLookupScrsId] = useState("");
  const [lookupUserAddedCarriers, setLookupUserAddedCarriers] = useState<
    string[]
  >([]);
  type LookupCarrierEntry = {
    id: string;
    selected: boolean;
    manuallyCreated: boolean;
    createdBy: string;
  };
  const [lookupRailCarriers, setLookupRailCarriers] = useState<
    Record<string, LookupCarrierEntry>
  >({});
  const [loadingLookupRailCarriers, setLoadingLookupRailCarriers] =
    useState(false);
  const [setDestinationToTroubleshoot, setDisplayAddInboundRailPanel] =
    useNitroCAStore((state) => [
      state.setDestinationToTroubleshoot,
      state.setDisplayAddInboundRailPanel,
    ]);

  if (disabled) {
    return <DisabledRailSettings location={location} />;
  }

  // Build display string: "StationName (carrier1, carrier2)" or just "StationName"
  let stationDisplay = "";
  if (location.stationState) {
    const carrierList = location.carriers?.map((carrier) => carrier.id);
    stationDisplay = carrierList?.length
      ? `${location.stationState} (${carrierList.join(", ")})`
      : location.stationState;
  }

  const handleOpenSearch = () => {
    if (isMobile) {
      setShowRailCarrierReviewPanel(false);
      setShowRailStationLookupPanel(true);
      return;
    }

    setDestinationToTroubleshoot({
      id: location.id,
      name: location.name,
      city: location.city,
      stateOrProvince: location.state,
      coordinate: location.coordinates as any,
      isCity: false,
      displayName: location.name,
      isSurroundingLocation: false,
      availableProducts: [],
      details: {
        ...location,
      },
    });
    setDisplayAddInboundRailPanel(true);
  };

  const handleLookupRailStationNext = async (station: {
    name: string;
    value: string;
    scrsId?: string | null;
    carriers?: string[];
  }) => {
    // create map of carriers to quickly lookup if one was manually created or not
    const existingCarrierManualState = new Map(
      (location.carriers || []).map((c) => [c.id, c.manuallyCreated]),
    );

    setSelectedLookupStation(station.value);
    setSelectedLookupScrsId(station.scrsId || "");
    setLookupUserAddedCarriers([]);
    setShowRailStationLookupPanel(false);
    setShowRailCarrierReviewPanel(true);
    setLoadingLookupRailCarriers(true);

    try {
      const stationCarriers = (station.carriers || [])
        .filter((id): id is string => Boolean(id))
        .map((id) => ({
          id,
          manuallyCreated: existingCarrierManualState.get(id) ?? false,
        }));

      if (stationCarriers.length > 0) {
        const carriersMap = stationCarriers.reduce<
          Record<string, LookupCarrierEntry>
        >((acc, carrier) => {
          if (!carrier?.id) return acc;
          acc[carrier.id] = {
            id: carrier.id,
            selected: true,
            manuallyCreated: carrier.manuallyCreated,
            createdBy: "",
          };
          return acc;
        }, {});

        setLookupRailCarriers(carriersMap);
      } else {
        const records = await NitroService.getCarriersByRailStation({
          stationState: station.value,
        });

        const carriersMap: Record<string, LookupCarrierEntry> = {};
        records.forEach((record) => {
          if (!record?.id) return;
          carriersMap[record.id] = {
            id: record.id,
            selected: true,
            manuallyCreated: record.manuallyCreated,
            createdBy: "",
          };
        });

        setLookupRailCarriers(carriersMap);
      }
    } catch (error) {
      console.error("Error loading carriers:", error);
      const fallbackCarriersMap: Record<string, LookupCarrierEntry> = {};
      (station.carriers || []).forEach((carrierId) => {
        if (!carrierId) return;
        fallbackCarriersMap[carrierId] = {
          id: carrierId,
          selected: true,
          manuallyCreated: existingCarrierManualState.get(carrierId) ?? false,
          createdBy: "",
        };
      });
      setLookupRailCarriers(fallbackCarriersMap);
    } finally {
      setLoadingLookupRailCarriers(false);
    }
  };

  const handleLookupCarrierToggle = (carrier: string) => {
    setLookupRailCarriers((prev) => {
      const existing = prev[carrier];
      return {
        ...prev,
        [carrier]: {
          id: carrier,
          selected: !(existing?.selected ?? false),
          manuallyCreated: existing?.manuallyCreated ?? false,
          createdBy: existing?.createdBy ?? "",
        },
      };
    });
  };

  const selectedLookupCarrierIds = Object.values(lookupRailCarriers)
    .filter((carrier) => carrier.selected)
    .map((carrier) => carrier.id);

  const selectedLookupCarrierRecords = Object.values(lookupRailCarriers)
    .filter((carrier) => carrier.selected)
    .map((carrier) => ({
      id: carrier.id,
      manuallyCreated: carrier.manuallyCreated,
      createdBy: carrier.createdBy,
    }));

  const lookupCarriersSelectedMap = Object.fromEntries(
    Object.entries(lookupRailCarriers).map(([carrierName, carrier]) => [
      carrierName,
      carrier.selected,
    ]),
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: 3,
      }}
    >
      {/* Station & Carrier(s) */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <div
          id="rail-station-carrier-label"
          style={{
            fontSize: 14,
            color: "#62748e",
            lineHeight: "14px",
          }}
        >
          Station &amp; Carrier(s)
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <div
            id="rail-station-carrier-display"
            style={{
              flex: "1 0 0",
              height: 25,
              background: "#f8fafc",
              border: "1.25px solid #e2e8f0",
              borderRadius: 3.5,
              display: "flex",
              alignItems: "center",
              padding: "4px 8px",
              fontSize: 14,
              color: stationDisplay ? "#0a0a0a" : "#878787",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {stationDisplay || (
              <span style={{ fontStyle: "italic" }}>No station selected</span>
            )}
          </div>
          <IconButton
            id="rail-station-search-button"
            data-testid="rail-station-search-button"
            onClick={handleOpenSearch}
            size="small"
            style={{
              width: 38,
              height: 34,
              background: "#ffffff",
              border: "1.25px solid #e2e8f0",
              borderRadius: 6.75,
              boxShadow:
                "0px 1px 2px 0px rgba(0,0,0,0.3), 0px 1px 3px 0px rgba(0,0,0,0.15)",
              flexShrink: 0,
            }}
          >
            <SearchIcon style={{ fontSize: 25 }} />
          </IconButton>
        </div>
      </div>

      {/* Equipment */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <div
          style={{
            fontSize: 14,
            color: "#62748e",
            lineHeight: "14px",
          }}
        >
          Equipment
        </div>
        <Select
          id="rail-equipment-type-select"
          size="small"
          value={location.equipmentTypes || []}
          multiple
          onChange={(e) => {
            setLocation({
              ...location,
              equipmentTypes: e.target.value as any,
            });
          }}
          renderValue={(selected) => selected.join(", ")}
          IconComponent={EquipmentDropdownIcon}
          style={{
            background: "#f8fafc",
            border: "1.25px solid #e2e8f0",
            borderRadius: 3.5,
            height: 32,
            fontSize: 14,
          }}
          MenuProps={{
            ...(isMobile
              ? {
                  sx: {
                    zIndex: 13050,
                  },
                  disableScrollLock: true,
                }
              : {}),
            PaperProps: {
              style: {
                maxHeight: 140,
                ...(isMobile ? { zIndex: 13051 } : {}),
              },
            },
          }}
        >
          <MenuItem value="SINGLE">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Checkbox
                sx={{ padding: 0, margin: 0 }}
                checked={location.equipmentTypes?.includes("SINGLE") || false}
              />
              <div>SINGLE</div>
            </div>
          </MenuItem>
          <MenuItem value="UNIT TRAIN">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Checkbox
                sx={{ padding: 0, margin: 0 }}
                checked={
                  location.equipmentTypes?.includes("UNIT TRAIN") || false
                }
              />
              <div>UNIT TRAIN</div>
            </div>
          </MenuItem>
        </Select>
      </div>

      {isMobile && (
        <>
          <RailStationLookupPanel
            open={showRailStationLookupPanel}
            destinationName={location.name || location.zipCode || "Destination"}
            defaultFilterLabel={`${location.city || ""}${
              location.state ? `, ${location.state}` : ""
            }`}
            selectedScrsId={isMobile ? location.scrsId || "" : ""}
            stations={[]}
            loading={false}
            onClose={() => setShowRailStationLookupPanel(false)}
            onNext={handleLookupRailStationNext}
            onDisplayStationsEmptyChange={() => {}}
          />

          <RailStationCarrierReviewPanel
            open={showRailCarrierReviewPanel}
            destinationName={location.name || location.zipCode || "Destination"}
            stationName={selectedLookupStation}
            carriers={lookupCarriersSelectedMap}
            loading={loadingLookupRailCarriers}
            locationId={location.id}
            selectedScrsId={selectedLookupScrsId || undefined}
            existingModes={
              toModes(location.inboundModes, location.outboundModes) as any
            }
            userAddedCarriers={lookupUserAddedCarriers}
            marketerName={`${auth.user?.profile?.given_name || ""} ${
              auth.user?.profile?.family_name || ""
            }`.trim()}
            isMobileFinishFlow={isMobile}
            onBack={() => {
              setShowRailCarrierReviewPanel(false);
              setShowRailStationLookupPanel(true);
            }}
            onClose={() => setShowRailCarrierReviewPanel(false)}
            onToggleCarrier={handleLookupCarrierToggle}
            onAddCarrier={() => {
              setShowRailCarrierReviewPanel(false);
              setShowRailAddCarrierPanel(true);
            }}
            onNext={() => {
              setLocation({
                ...location,
                stationState: selectedLookupStation,
                carriers: selectedLookupCarrierRecords,
              });
              setShowRailCarrierReviewPanel(false);
            }}
            onFinishSuccess={({ railStation, carriers, scrsId }) => {
              setLocation({
                ...location,
                stationState: railStation,
                carriers: carriers.map((carrierId) => ({
                  id: carrierId,
                  manuallyCreated:
                    lookupRailCarriers[carrierId]?.manuallyCreated ?? false,
                  createdBy: lookupRailCarriers[carrierId]?.createdBy ?? "",
                })),
                scrsId: scrsId || location.scrsId,
              });
              setShowRailCarrierReviewPanel(false);
            }}
            onFinishError={() => {
              setShowRailCarrierReviewPanel(true);
            }}
          />

          <RailStationAddCarrierPanel
            open={showRailAddCarrierPanel}
            initialSelectedCarriers={selectedLookupCarrierIds}
            onBack={(selectedCarriers) => {
              const existingCarrierNames = Object.keys(lookupRailCarriers);
              const newlyAdded = selectedCarriers.filter(
                (carrier) => !existingCarrierNames.includes(carrier),
              );
              if (newlyAdded.length > 0) {
                setLookupUserAddedCarriers((prev) =>
                  Array.from(new Set([...prev, ...newlyAdded])),
                );
              }
              setLookupRailCarriers((prev) => {
                const next = { ...prev };
                selectedCarriers.forEach((carrier) => {
                  const existing = next[carrier];
                  next[carrier] = {
                    id: carrier,
                    selected: true,
                    manuallyCreated: existing?.manuallyCreated ?? true,
                    createdBy: existing?.createdBy ?? "",
                  };
                });
                return next;
              });
              setShowRailAddCarrierPanel(false);
              setShowRailCarrierReviewPanel(true);
            }}
            onApplySelected={async (selectedCarriers) => {
              const existingCarrierNames = Object.keys(lookupRailCarriers);
              const newlyAdded = selectedCarriers.filter(
                (carrier) => !existingCarrierNames.includes(carrier),
              );

              if (selectedCarriers.length > 0 && selectedLookupStation) {
                await NitroService.createCarrierForStation({
                  stationState: selectedLookupStation,
                  carriers: selectedCarriers,
                  username: `${auth.user?.profile?.given_name || ""} ${
                    auth.user?.profile?.family_name || ""
                  }`.trim(),
                });
              }

              if (newlyAdded.length > 0) {
                setLookupUserAddedCarriers((prev) =>
                  Array.from(new Set([...prev, ...newlyAdded])),
                );
              }
              setLookupRailCarriers((prev) => {
                const next = { ...prev };
                selectedCarriers.forEach((carrier) => {
                  const existing = next[carrier];
                  next[carrier] = {
                    id: carrier,
                    selected: true,
                    manuallyCreated: existing?.manuallyCreated ?? true,
                    createdBy: existing?.createdBy ?? "",
                  };
                });
                return next;
              });
              setShowRailAddCarrierPanel(false);
              setShowRailCarrierReviewPanel(true);
            }}
            onClose={() => {
              setShowRailAddCarrierPanel(false);
              setShowRailCarrierReviewPanel(true);
            }}
          />
        </>
      )}
    </div>
  );
};

export default RailSettingsOLD;
